// frontend/src/utils/categoryEditing.js
//
// The questions every screen that shows an item category has to answer, all
// answered from the store's capability (`useCategoryEditing()` →
// core.category_editing on the backend) rather than from `category_source`:
//
//   writesCategory(capability, field)    — is THIS column the category write?
//   categoryCellLabel(capability, value) — what does that cell READ as?
//   categoryWrite(capability, picked)    — what payload does the pick produce?
//
// The last two are the same fact from both ends: the cell value is an option
// object where a taxonomy node is assigned and a plain string where a product
// type is. Knowing that in one module is the point — a renderer that unpacked
// `.display_label` itself would go quietly blank the day the shape moved.
//
// NOT KNOWING YET is a real state: `useCategoryEditing()` is null until a store
// is selected, and the app's route guards let a page render before that. The
// two kinds of question answer it differently, and the difference is the whole
// safety argument:
//
//   writesCategory — asked while DECIDING WHAT TO RENDER. Without a capability
//     it is false, which every caller renders as "no category affordance here".
//     That is total and safe by construction: no write is promised, so there is
//     nothing to be wrong about. It lives here rather than as a `capability &&`
//     at each call site, because three sites repeating it is three chances for
//     one of them to forget.
//
//   categoryCellLabel / categoryWrite — asked while ACTING on a value. A guess
//     there reads or posts the wrong field, so they throw. They are only reachable through a
//     rendered affordance, which the rule above already withheld.
//
// Pure functions with no React or query dependency on purpose: the Data Health
// resolution logic (datahealth/resolveIssue) and the grid pages both need them,
// and the former is unit-tested without a DOM. The cell itself — the picker,
// its option source, its create row — is utils/categoryPickerColDef.

/** Guard for the two acting functions — see the header for why they refuse. */
function requireCapability(capability, fn) {
  if (!capability?.write_field) {
    throw new Error(
      `${fn}: needs the store's category_editing capability (got `
      + `${JSON.stringify(capability)}) — a screen without one must render no `
      + 'category affordance, not a guessed one',
    );
  }
}

/**
 * Is this cell value the shape this store's category cell holds — an option
 * object where a taxonomy node is assigned, a plain string where a product type
 * is? Reported once, at ERROR, wherever it is asked: reading and writing are the
 * same defect seen from two ends, so neither may treat it as ordinary.
 *
 * Reachable when a category-source rebuild completes under a screen holding
 * dirty picks (Store Settings refreshes the capability the moment its poll says
 * the switch is done); the cells then hold values chosen under the old one.
 */
function holdsOwnShape(capability, value, fn) {
  const wanted = capability.write_field === 'item_type' ? 'string' : 'object';
  if (typeof value === wanted) return true;
  console.error(
    `${fn}: this store writes its category as `
    + `${wanted === 'string' ? 'a product type (a string)' : 'a taxonomy node (an option object)'}`
    + ', but the cell holds', value, '— ignoring it.',
  );
  return false;
}

/**
 * Is `field` the column that carries the category write on this store?
 *
 * The replacement for every `categorySource === 'product_type'` branch: a
 * screen asks about the column in front of it, not about the store's mode.
 * On a PRODUCT_TYPE store `item_type` IS the category (the backend derives
 * one from the other), so the product-type column carries the picker and the
 * category column is a read-only mirror; on a taxonomy store it is the other
 * way round, and `item_type` is an ordinary Shopify passthrough field.
 *
 * False when the capability is not known yet — see the module header for why
 * that is an answer here and a throw everywhere else.
 *
 * @param {{write_field: string}|null|undefined} capability
 * @param {string} field
 */
export function writesCategory(capability, field) {
  return capability?.write_field === field;
}

/**
 * What the category cell READS as — the merchant-facing label of whatever it
 * currently holds.
 *
 * The counterpart to `categoryWrite`: one module knows that the cell holds an
 * option object where a taxonomy node is assigned and a plain string where a
 * product type is, so a renderer never has to. Empty string when the cell holds
 * nothing — callers render their own placeholder (grids use an em dash).
 *
 * @param {{write_field: string}} capability
 * @param {object|string|null|undefined} value  the cell value
 * @returns {string}
 */
export function categoryCellLabel(capability, value) {
  requireCapability(capability, 'categoryCellLabel');
  if (value == null) return '';
  if (!holdsOwnShape(capability, value, 'categoryCellLabel')) return '';
  return capability.write_field === 'item_type' ? value : (value.display_label ?? '');
}

/**
 * What the merchant picked → the field write for this store.
 *
 * The single translation from cell value to payload, so no call site repeats
 * the capability's `write_field` or its value shape (an option object for the
 * taxonomy, a plain string for a product type). Returns an EMPTY object when
 * nothing is picked: an untouched cell contributes no field to the batch,
 * which is what keeps a bulk save from clearing what it never asked about.
 *
 * A value of the OTHER capability's shape writes nothing and is reported
 * (`holdsOwnShape`): `String(anObject)` would have posted the literal
 * "[object Object]" as a merchant's product type, and the backend has no way to
 * tell that from a type they meant.
 *
 * @param {{write_field: string}} capability
 * @param {object|string|null|undefined} picked  the cell value
 * @returns {object} fields to merge into the item payload
 */
export function categoryWrite(capability, picked) {
  requireCapability(capability, 'categoryWrite');
  if (picked == null) return {};
  if (!holdsOwnShape(capability, picked, 'categoryWrite')) return {};

  if (capability.write_field === 'item_type') {
    const type = picked.trim();
    return type ? { item_type: type } : {};
  }
  return picked.id ? { product_category_id: picked.id } : {};
}
